import React, {useEffect, useRef, useState} from "react";
import { Organization, Role, User, UserRole } from "../../../types/userTypes";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import CustomAutoComplete from "../CommonComponents/CustomAutoComplete";
import Button from "@mui/material/Button";
import { Alert, Tooltip } from "@mui/material";
import RoleRendering from "./RoleRendering";
import {OP_BUTTON_STYLE} from "@/constants/styles";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";

interface UserFormProps {
    user: User;
    roles: Role[];
    organizations: Organization[];
    onSubmit: () => void;
    sendFormLabel?: string;
    mode: "update" | "create" | "passwordChange";
    onInfoChange: (field: keyof User, value: string) => void;
    onRolesChange: (roles: Role[]) => void;
    onOrganizationsChange: (organization: Organization[]) => void;
    onCloseClick: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
                                               user,
                                               roles,
                                               organizations,
                                               onSubmit,
                                               sendFormLabel = '',
                                               onInfoChange,
                                               onRolesChange,
                                               mode,
                                               onOrganizationsChange,
                                                onCloseClick,
                                           }) => {

    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);
    const [orgMessage, setOrgMessage] = React.useState<string>("");
    const initialOrganization = useRef(user.organizations?.[0]);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>, field: keyof User) {
        onInfoChange(field, e.target.value);
    }

    function handleRoleChange(role: Role) {
        onRolesChange([role]);
    }

    function handleOrganizationChange(organization: Organization) {
        if (user.organizations !== undefined) {
            if(!organization && mode === 'create'){
                setOrgMessage("")
            }
            else if (organization && initialOrganization.current && organization.id !== initialOrganization.current.id && mode !== 'create') {
                setOrgMessage("Warning, you are overwriting the user's organization");
            }
            else if(organization && initialOrganization.current  && organization.id === initialOrganization.current.id){
                setOrgMessage("")
            }
        }
        onOrganizationsChange([organization]);
    }

    function handleOnSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        onSubmit();
    }

    function handleChangePasswordConfirm(value: string) {
        setPasswordConfirm(value);
    }

    useEffect(() => {
        if (mode === 'passwordChange') {
            if (passwordConfirm === user.password) setButtonDisabled(false);
            else setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }
    }, [passwordConfirm, user.password, mode]);

    return (
        <Box
            component="form"
            onSubmit={handleOnSubmit}
            data-tour={"users_dialog_creation_user"}
        >
            {orgMessage !== "" && (
                <Alert variant="filled" severity="warning" sx={{marginBottom: 3}}>
                    {orgMessage}
                </Alert>
            )}

            <Grid
                container
                spacing={2}
                sx={{ marginBottom: 3, marginTop: 1 }}
            >
                {(mode === 'update' || mode === 'create') && (
                    <>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'email')}
                                value={user.email}
                                name="email"
                                placeholder="Email"
                                label="E-mail"
                                disabled={sendFormLabel.includes("Update")}
                                type="email"
                                variant="outlined"
                                required={true}
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'username')}
                                disabled={sendFormLabel.includes("Update")}
                                value={user.username}
                                name="username"
                                label="Username"
                                placeholder="Username"
                                type="text"
                                variant="outlined"
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'name')}
                                value={user.name}
                                name="name"
                                placeholder="Name"
                                label={"Name"}
                                type="text"
                                variant="outlined"
                                required={true}
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'surname')}
                                value={user.surname}
                                name="surname"
                                label={"Surname"}
                                placeholder="Surname"
                                type="text"
                                variant="outlined"
                                required={true}
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                    </>
                )}

                {(mode === 'create' || mode === 'passwordChange') && (
                    <>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e, 'password')}
                                value={user.password}
                                name="password"
                                placeholder="password"
                                type="password"
                                label={"Password"}
                                variant="outlined"
                                required={true}
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <TextField
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangePasswordConfirm(e.target.value)}
                                value={passwordConfirm}
                                name="password confirm"
                                placeholder="Confirm Password"
                                type="password"
                                variant="outlined"
                                required={true}
                                label={"Confirm Password"}
                                fullWidth
                                size="medium"
                                sx={{ marginBottom: 3 }}
                            />
                        </Grid>
                    </>
                )}

                {(mode === 'update' || mode === 'create') && (
                    <>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <CustomAutoComplete
                                onChange={(_: any, newValue: any) => handleRoleChange(newValue)}
                                value={user.roles[0] ? user.roles[0] : { id: "", name: "" }}
                                label="Roles"
                                name="roles"
                                options={roles}
                                labelField="name"
                                valueField="id"
                                required={true}
                                multiple={false}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
                            <RoleRendering roles={[UserRole.SuperAdmin]}>
                                <CustomAutoComplete
                                    onChange={(_: any, newValue: any) => handleOrganizationChange(newValue)}
                                    value={user.organizations![0] ? user.organizations![0] : { id: "", name: "" }}
                                    label="Organization"
                                    name="organizations"
                                    options={organizations}
                                    labelField="name"
                                    valueField="id"
                                    required={true}
                                    multiple={false}
                                />
                            </RoleRendering>
                        </Grid>
                    </>
                )}
            </Grid>

            <Box width="100%" display="flex" alignItems="center" justifyContent="flex-end">
                <Tooltip
                    title="Passwords must match to enable the submit button."
                    disableHoverListener={!buttonDisabled}
                >
                    <Box display={'flex'} gap={1}>
                        <Button
                            disabled={buttonDisabled}
                            variant="contained"
                            type="submit"
                            sx={OP_BUTTON_STYLE}
                            startIcon={<SaveIcon />}
                        >
                            {sendFormLabel}
                        </Button>
                        <Button
                            variant="contained"
                            type="submit"
                            sx={OP_BUTTON_STYLE}
                            onClick={onCloseClick}
                            startIcon={<CancelIcon/>}
                            data-tour={"users_creational_form_close"}
                        >
                            Close
                        </Button>
                    </Box>

                </Tooltip>
            </Box>
        </Box>
    );
};

export default UserForm;